<template>
  <div class="escolher-endereco">
    <!-- Resumo do endereço escolhido (quando alternativo) -->
    <b-alert v-if="value" show variant="info" class="mb-50 p-75">
      <div class="d-flex align-items-start justify-content-between">
        <div>
          <strong>Entrega em endereço alternativo:</strong><br>
          {{ resumo(value) }}
        </div>
        <b-button variant="flat-danger" size="sm" class="ml-50" @click="limpar">
          Usar endereço padrão
        </b-button>
      </div>
    </b-alert>

    <b-button variant="outline-primary" size="sm" @click="abrir">
      <feather-icon icon="MapPinIcon" size="14" class="mr-25" />
      {{ value ? 'Trocar endereço de entrega' : 'Alterar endereço de entrega' }}
    </b-button>

    <b-modal
      v-model="modalAberto"
      title="Endereço de entrega"
      size="lg"
      hide-footer
      @hidden="$emit('fechado')"
    >
      <b-alert show variant="secondary" class="p-75">
        <template v-if="String(ufAtual || '').trim()">
          A troca de endereço só é permitida dentro da mesma UF
          (<strong>{{ ufAtual }}</strong>), pois os impostos do pedido são
          calculados pela UF de destino.
        </template>
        <template v-else>
          Este cliente não tem endereço padrão cadastrado — selecione um endereço
          já cadastrado ou informe um novo (qualquer UF). Os impostos serão
          calculados pela UF do endereço escolhido.
        </template>
      </b-alert>

      <b-tabs>
        <!-- Endereços cadastrados -->
        <b-tab title="Endereços cadastrados" active>
          <b-overlay :show="carregando" rounded="sm">
            <div v-if="!carregando && enderecosMesmaUf.length === 0" class="text-muted py-1">
              Nenhum outro endereço cadastrado nesta UF para este cliente.
            </div>
            <b-list-group v-else class="mt-50">
              <b-list-group-item
                v-for="(end, idx) in enderecosMesmaUf"
                :key="idx"
                button
                @click="selecionarCadastrado(end)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ end.clienteendereco }}<template v-if="end.clienteendereconumero">, {{ end.clienteendereconumero }}</template></strong>
                    <span v-if="end.clienteenderecocomplemento"> — {{ end.clienteenderecocomplemento }}</span><br>
                    <small class="text-muted">
                      {{ end.clienteenderecobairro }} · {{ end.clienteenderecocidade }}/{{ end.ufid }} · CEP {{ end.clienteenderecocep }}
                    </small>
                  </div>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </div>
              </b-list-group-item>
            </b-list-group>
          </b-overlay>
        </b-tab>

        <!-- Novo endereço (CEP primeiro) -->
        <b-tab title="Novo endereço">
          <b-row class="mt-50">
            <b-col md="4">
              <b-form-group label="CEP *">
                <b-input-group>
                  <b-form-input
                    v-model="novo.cep"
                    placeholder="Somente números"
                    maxlength="9"
                    @input="onCepInput"
                  />
                  <b-input-group-append v-if="buscandoCep" is-text>
                    <b-spinner small />
                  </b-input-group-append>
                </b-input-group>
                <small v-if="cepErro" class="text-danger">{{ cepErro }}</small>
              </b-form-group>
            </b-col>
            <b-col md="8">
              <b-form-group label="Endereço (logradouro) *">
                <b-form-input v-model="novo.endereco" :disabled="!cepConsultado" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Número *">
                <b-form-input v-model="novo.numero" :disabled="!cepConsultado" />
              </b-form-group>
            </b-col>
            <b-col md="5">
              <b-form-group label="Complemento">
                <b-form-input v-model="novo.complemento" :disabled="!cepConsultado" />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Bairro *">
                <b-form-input v-model="novo.bairro" :disabled="!cepConsultado" />
              </b-form-group>
            </b-col>
            <b-col md="5">
              <b-form-group label="Cidade">
                <b-form-input v-model="novo.cidade" disabled />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="UF">
                <b-form-input v-model="novo.uf" disabled />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="text-right mt-50">
            <b-button
              variant="primary"
              :disabled="!novoValido"
              @click="confirmarNovo"
            >
              Usar este endereço
            </b-button>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BModal, BTabs, BTab, BAlert, BListGroup, BListGroupItem,
  BOverlay, BRow, BCol, BFormGroup, BFormInput, BInputGroup, BInputGroupAppend, BSpinner,
} from 'bootstrap-vue';
import SlsClienteEnderecoClient from '@/api/Cadastros/Cliente/sls-cliente-endereco.client';

export default {
  name: 'EscolherEnderecoEntrega',
  components: {
    BButton, BModal, BTabs, BTab, BAlert, BListGroup, BListGroupItem,
    BOverlay, BRow, BCol, BFormGroup, BFormInput, BInputGroup, BInputGroupAppend, BSpinner,
  },
  props: {
    // Endereço alternativo escolhido ({endereco,numero,complemento,bairro,cidade,uf,cep}) ou null
    value: { type: Object, default: null },
    // PK interna do cliente (sls_Cliente.clienteid) para buscar os endereços
    clienteid: { type: [String, Number], default: '' },
    // UF do endereço padrão (troca só na mesma UF)
    ufAtual: { type: String, default: '' },
  },
  data() {
    return {
      modalAberto: false,
      carregando: false,
      enderecos: [],
      buscandoCep: false,
      cepConsultado: false,
      cepErro: '',
      novo: { cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' },
    };
  },
  computed: {
    enderecosMesmaUf() {
      const uf = String(this.ufAtual || '').trim().toUpperCase();
      // Cliente SEM endereço padrão (UF vazia): não há UF de referência —
      // mostra todos os endereços cadastrados em vez de esconder tudo.
      if (!uf) return (this.enderecos || []).filter((e) => e && e.clienteendereco);
      return (this.enderecos || []).filter(
        (e) => e && e.clienteendereco && String(e.ufid || '').trim().toUpperCase() === uf,
      );
    },
    novoValido() {
      const ufAtual = String(this.ufAtual || '').trim().toUpperCase();
      return this.cepConsultado
        && !this.cepErro
        && String(this.novo.endereco || '').trim()
        && String(this.novo.numero || '').trim()
        && String(this.novo.bairro || '').trim()
        // Sem endereço padrão (UF vazia) qualquer UF vale; senão, mesma UF.
        && (!ufAtual || String(this.novo.uf || '').trim().toUpperCase() === ufAtual);
    },
  },
  methods: {
    resumo(e) {
      const partes = [
        `${e.endereco}${e.numero ? `, ${e.numero}` : ''}`,
        e.complemento,
        e.bairro,
        `${e.cidade}/${e.uf}`,
        `CEP ${e.cep}`,
      ].filter(Boolean);
      return partes.join(' — ');
    },
    limpar() {
      this.$emit('input', null);
    },
    async abrir() {
      this.modalAberto = true;
      if (!this.clienteid) return;
      this.carregando = true;
      try {
        const client = new SlsClienteEnderecoClient();
        const body = await client.getEnderecosPorCliente(this.clienteid);
        const lista = body?.data || body || [];
        this.enderecos = Array.isArray(lista) ? lista : [];
      } catch (e) {
        this.enderecos = [];
      } finally {
        this.carregando = false;
      }
    },
    selecionarCadastrado(end) {
      this.$emit('input', {
        endereco: String(end.clienteendereco || '').trim(),
        numero: String(end.clienteendereconumero || '').trim(),
        complemento: String(end.clienteenderecocomplemento || '').trim(),
        bairro: String(end.clienteenderecobairro || '').trim(),
        cidade: String(end.clienteenderecocidade || '').trim(),
        uf: String(end.ufid || '').trim().toUpperCase(),
        cep: String(end.clienteenderecocep || '').trim(),
      });
      this.modalAberto = false;
    },
    async onCepInput() {
      this.cepErro = '';
      this.cepConsultado = false;
      const cep = String(this.novo.cep || '').replace(/\D/g, '');
      if (cep.length !== 8) return;
      this.buscandoCep = true;
      try {
        const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await resp.json();
        if (data.erro) {
          this.cepErro = 'CEP não encontrado.';
          return;
        }
        const uf = String(data.uf || '').toUpperCase();
        const ufAtual = String(this.ufAtual || '').trim().toUpperCase();
        // Sem endereço padrão (UF vazia) qualquer UF é aceita.
        if (ufAtual && uf !== ufAtual) {
          this.cepErro = `Este CEP é de ${uf}; a entrega deste pedido só pode ser alterada dentro de ${this.ufAtual} (impostos calculados pela UF).`;
          return;
        }
        this.novo.endereco = data.logradouro || '';
        this.novo.bairro = data.bairro || '';
        this.novo.cidade = data.localidade || '';
        this.novo.uf = uf;
        this.cepConsultado = true;
      } catch (e) {
        this.cepErro = 'Falha ao consultar o CEP. Tente novamente.';
      } finally {
        this.buscandoCep = false;
      }
    },
    confirmarNovo() {
      if (!this.novoValido) return;
      this.$emit('input', {
        endereco: String(this.novo.endereco).trim(),
        numero: String(this.novo.numero).trim(),
        complemento: String(this.novo.complemento || '').trim(),
        bairro: String(this.novo.bairro).trim(),
        cidade: String(this.novo.cidade).trim(),
        uf: String(this.novo.uf).trim().toUpperCase(),
        cep: String(this.novo.cep).replace(/\D/g, ''),
      });
      this.modalAberto = false;
      this.novo = { cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };
      this.cepConsultado = false;
    },
  },
};
</script>
